// components/UserActionDialog.tsx
import { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Info, Loader2Icon, AlertTriangle } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { Textarea } from './ui/textarea'
import { showErrorToast } from './ui/toast'
import { cn } from '@/lib/utils'
import { SettingsIcon } from './Icons'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { useQuery } from '@tanstack/react-query'
import { getAdvertisementFrequency } from '@/api/sponsorApi'
import { Alert, AlertDescription } from '@/components/ui/alert'

type ActionType = 'block' | 'suspended' | 'banned'

interface UserActionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type?: ActionType
  onConfirm: (formData: {
    duration: number
    reason: string
    note?: string
  }) => void
  isLoading?: boolean
  title: string
  subtext: string
  confirmButtonText: string
  danger?: boolean
  showForm?: boolean
  rotationFrequency?: boolean
  generalIcon?: boolean
  rejectAdReason?: boolean
  showWarning?: boolean
  warningMessage?: string
}

type FormValues = {
  duration: string
  reason: string
  notes?: string
  heroFlight?: string
  inFeedBursts?: string
  rightRail?: string
}

export function UserActionDialog({
  open,
  onOpenChange,
  type,
  onConfirm,
  isLoading = false,
  title,
  subtext,
  confirmButtonText,
  danger = true,
  showForm = true,
  rotationFrequency = false,
  generalIcon = false,
  rejectAdReason = false,
  showWarning = false,
  warningMessage = 'You are about to lock the Top 100 submissions. Please review the submissions before proceeding.',
}: Readonly<UserActionDialogProps>) {
  const isSuspend = type === 'suspended'

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      duration: undefined,
      reason: '',
      notes: '',
    },
  })

  const { data: frequencyData } = useQuery({
    queryKey: SPONSOR_QUERY_KEYS.advertisementFrequency,
    queryFn: getAdvertisementFrequency,
    enabled: rotationFrequency && open,
  })

  useEffect(() => {
    if (open) {
      if (rotationFrequency && frequencyData?.data?.rotationFrequency) {
        reset({
          duration: frequencyData.data.rotationFrequency,
          reason: '',
          notes: '',
        })
      } else {
        reset()
      }
    }
  }, [open, type, reset, rotationFrequency, frequencyData])

  // 🎯 Suspend User Mutation

  const onSubmit = async (data: FormValues) => {
    try {
      const formattedData = {
        duration:
          data.duration && data.duration !== ''
            ? Number.parseInt(data.duration, 10)
            : 0,
        reason: data.reason,
        notes: data.notes,
      }
      onConfirm(formattedData)
    } catch {
      const actionText = isSuspend ? 'suspend' : 'block'
      showErrorToast(`Failed to ${actionText} user. Please try again.`)
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[90%] max-w-[470px]"
        role="<dialog>"
        aria-modal="true"
        aria-labelledby="user-action-dialog-title"
        aria-describedby="user-action-dialog-desc"
      >
        <DialogHeader>
          {generalIcon ? (
            <SettingsIcon
              className={cn(
                'mb-2 h-9 w-9 p-2 rounded-full text-ss-brand-200 bg-ss-brand-50'
              )}
              aria-hidden="true"
            />
          ) : (
            <Info
              className={cn(
                'mb-2 h-9 w-9 p-2 rounded-full',
                danger
                  ? 'text-ss-critical-200 bg-ss-critical-100'
                  : 'text-ss-warning-200 bg-ss-warning-100'
              )}
              strokeWidth={1.5}
              aria-hidden="true"
            />
          )}
          <DialogTitle
            id="user-action-dialog-title"
            className={cn('text-base font-semibold text-ss-neutral-950')}
          >
            {title}
          </DialogTitle>
          <DialogDescription
            id="user-action-dialog-desc"
            className={cn('text-sm font-normal text-black/88')}
          >
            {subtext}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 py-2"
          aria-label={isSuspend ? 'Suspend user form' : 'Block user form'}
        >
          {showForm && (
            <>
              {isSuspend && (
                <div className="flex flex-col gap-2">
                  <Label variant={'required'} htmlFor="duration">
                    Suspension Duration
                  </Label>
                  <Controller
                    name="duration"
                    control={control}
                    rules={{
                      required: isSuspend
                        ? 'Suspension duration is required'
                        : false,
                    }}
                    render={({ field }) => (
                      <div>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value as string}
                        >
                          <SelectTrigger
                            id="duration"
                            className="h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 w-full"
                            aria-label="Suspension duration"
                          >
                            <SelectValue placeholder="Select suspension duration" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1d">1 Day</SelectItem>
                            <SelectItem value="7d">7 Days</SelectItem>
                            <SelectItem value="30d">30 Days</SelectItem>
                            <SelectItem value="permanent">Permanent</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.duration && (
                          <p
                            className="text-xs text-ss-critical-200 mt-1"
                            role="alert"
                          >
                            {errors.duration.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Label htmlFor="reason" variant={'required'}>
                  Reason
                </Label>
                <Controller
                  name="reason"
                  control={control}
                  rules={{ required: 'Reason is required' }}
                  render={({ field }) => (
                    <div>
                      <Input
                        id="reason"
                        placeholder="Enter reason"
                        {...field}
                        aria-required="true"
                        aria-label="Reason for action"
                        maxLength={255}
                      />
                      {errors.reason && (
                        <p
                          className="text-xs text-ss-critical-200 mt-1"
                          role="alert"
                        >
                          {errors.reason.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="note">Note</Label>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      id="note"
                      placeholder="Enter note"
                      className="resize-none break-all h-24 overflow-auto border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 w-full "
                      {...field}
                      aria-label="Additional note"
                    />
                  )}
                />
              </div>
            </>
          )}
          {rotationFrequency && (
            <div className="flex flex-col gap-2">
              <Label variant={'required'} htmlFor="rotationFrequency">
                Ad Rotation Frequency
              </Label>
              <Controller
                name="duration"
                control={control}
                rules={{
                  required: isSuspend
                    ? 'Suspension duration is required'
                    : false,
                }}
                render={({ field }) => (
                  <div>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value as string}
                    >
                      <SelectTrigger
                        id="duration"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 w-full"
                        aria-label="Suspension duration"
                      >
                        <SelectValue placeholder="Select suspension duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">03s</SelectItem>
                        <SelectItem value="10">10s</SelectItem>
                        <SelectItem value="20">20s</SelectItem>
                        <SelectItem value="30">30s</SelectItem>
                        <SelectItem value="40">40s</SelectItem>
                        <SelectItem value="50">50s</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.duration && (
                      <p
                        className="text-xs text-ss-critical-200 mt-1"
                        role="alert"
                      >
                        {errors.duration.message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}
          {rejectAdReason && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="reason">Reason for Rejection</Label>
              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <div>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value as string}
                    >
                      <SelectTrigger
                        id="duration"
                        className="h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 w-full"
                        aria-label="Suspension duration"
                      >
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Spam">Spam</SelectItem>
                        <SelectItem value="Inappropriate Content">
                          Inappropriate Content
                        </SelectItem>
                        <SelectItem value="Misleading Information">
                          Misleading Information
                        </SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              />
            </div>
          )}

          {showWarning && (
            <Alert variant="warning" className="p-3 has-[>svg]:gap-x-2">
              <AlertTriangle />
              <AlertDescription className="text-xs font-medium leading-4">
                {warningMessage}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              aria-label="Cancel action"
              className="rounded-lg"
              // className="min-w-[110px] h-12 text-lg font-medium border-2 border-ss-neutral-100"
            >
              Cancel
            </Button>
            <Button
              disabled={isLoading}
              type="submit"
              variant={danger ? 'destructive' : 'default'}
              aria-label={confirmButtonText}
              className="rounded-lg"
              // className="min-w-[140px] h-12 text-lg font-medium bg-ss-neutral-950 text-white hover:bg-ss-neutral-900"
            >
              {isLoading ? <Loader2Icon className="animate-spin" /> : ''}
              {confirmButtonText}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
